import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScrambleGlitchEffect } from "../UX";
import { Image, Box } from "@chakra-ui/react";
import TK_main_pic from "../../assets/TK_main_pic.png";
import TK_poly_pic from "../../assets/TK_poly_pic.png";
import { Web_logo } from "../../assets";
import "./header.css";

function Header() {
  const [showMain, setShowMain] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);

    setTimeout(() => {
      setShowMain(prev => !prev);
    }, 400);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 800);
  }, [isTransitioning]);

  return (
    <>
      <div className="header-content">
        <div 
          className="header-name"
          style={{ 
            minHeight: "clamp(40px, 8vw, 80px)", 
            display: "flex",
          }}
        >
          <ScrambleGlitchEffect originalText="TREBOR KATALBAS" />
        </div>

        {/* Title */}
        <div className="header-text slide-in-blurred-left">
          <div className="header-parts">
            <Image
              src={Web_logo}
              alt="Web Logo"
              boxSize={{ base: "30px", sm: "60px" }}
            />
            <h1 className="pl-2">WEB</h1>
            <h1>ARTISAN</h1>
          </div>
        </div>

        {/* Skills */}
        <div className="skills-container flex-container">
          <div className="skill-item container-bg">
            <h3>System / Web / App</h3>
          </div>
        </div>

        <div className="skills-container flex-container">
          <div className="skill-item container-bg">
            <h3>Developer</h3>
          </div>

          <div className="skill-item container-bg">
            <h3>Designer</h3>
          </div>
        </div>

        <div className="skills-container flex-container">
          <div className="skill-item container-br">
            <h3>4+ years of experience</h3>
          </div>

          <div className="skill-item container-br">
            <h3>Freelancer</h3>
          </div>
        </div>
      </div>

      <div 
        className="main-img"
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          className="circular-image-container"
          onClick={triggerTransition}
          position="relative"
          width={{ base: "250px", sm: "300px", md: "400px" }}
          height={{ base: "250px", sm: "300px", md: "400px" }}
          borderRadius="50%"
          overflow="hidden"
          cursor="pointer"
          mx="auto"
          sx={{
            "&:hover": {
              transform: "scale(1.02)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            transition: "transform 0.3s ease",
          }}
        >
          {/* Poly Image */}
          <motion.div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: showMain ? 1 : 2,
            }}
            animate={{
              opacity: showMain ? 0 : 1,
              scale: showMain ? 1.15 : 1,
              filter: showMain ? "blur(8px)" : "blur(0px)",
            }}
            transition={{ 
              duration: 0.8,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <Image
              src={TK_poly_pic}
              alt="Poly Art"
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius="50%"
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              borderRadius="50%"
              background="radial-gradient(circle, transparent 60%, rgba(0, 0, 0, 0.5) 100%)"
              pointerEvents="none"
            />
          </motion.div>

          {/* Main Image */}
          <motion.div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: showMain ? 2 : 1,
            }}
            animate={{
              opacity: showMain ? 1 : 0,
              scale: showMain ? 1 : 1.15,
              filter: showMain ? "blur(0px)" : "blur(8px)",
            }}
            transition={{ 
              duration: 0.8,
              ease: [0.4, 0, 0.2, 1],
            }}
          >
            <Image
              src={TK_main_pic}
              alt="Trebor Katalbas"
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius="50%"
            />
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              borderRadius="50%"
              background="radial-gradient(circle, transparent 60%, rgba(0, 0, 0, 0.5) 100%)"
              pointerEvents="none"
            />
          </motion.div>

          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            borderRadius="50%"
            zIndex={5}
            pointerEvents="none"
            sx={{
              boxShadow: `
                inset 0 0 40px 15px rgba(0, 0, 0, 0.6),
                inset 0 0 80px 30px rgba(0, 0, 0, 0.3)
              `,
              background: `
                radial-gradient(
                  circle at 50% 50%,
                  transparent 50%,
                  rgba(0, 0, 0, 0.2) 70%,
                  rgba(0, 0, 0, 0.4) 85%,
                  rgba(0, 0, 0, 0.6) 100%
                )
              `,
            }}
          />

          <AnimatePresence>
            {isTransitioning && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.15, 0] }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  background: "radial-gradient(circle, rgba(103, 120, 203, 0.3), transparent 70%)",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              />
            )}
          </AnimatePresence>

          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            borderRadius="50%"
            zIndex={3}
            pointerEvents="none"
            opacity={0.03}
            sx={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
              backgroundSize: "256px 256px",
            }}
          />

          <AnimatePresence>
            {!isTransitioning && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.5, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                style={{
                  position: "absolute",
                  bottom: "10%",
                  left: "55%",
                  transform: "translateX(-60%)",
                  background: "rgba(0, 0, 0, 0.6)",
                  backdropFilter: "blur(10px)",
                  padding: "1px 10px",
                  borderRadius: "20px",
                  zIndex: 20,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ 
                  color: "white", 
                  fontSize: "9px", 
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}>
                  {showMain ? "Revert" : "Tap me"}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </div>
    </>
  );
}

export default Header;